/**
 * Clipboard helper that works on desktop and mobile browsers.
 *
 * The async Clipboard API is used when available, with a selection based
 * `document.execCommand('copy')` fallback for older or non-secure contexts.
 */

function fallbackCopy(text: string): boolean {
	let textarea = document.createElement('textarea');
	textarea.value = text;

	// readonly keeps the on-screen keyboard closed on mobile, and a 16px font
	// stops iOS from zooming into the off-screen field.
	textarea.setAttribute('readonly', '');
	textarea.style.position = 'fixed';
	textarea.style.top = '0';
	textarea.style.left = '0';
	textarea.style.width = '1px';
	textarea.style.height = '1px';
	textarea.style.padding = '0';
	textarea.style.border = 'none';
	textarea.style.outline = 'none';
	textarea.style.boxShadow = 'none';
	textarea.style.background = 'transparent';
	textarea.style.fontSize = '16px';

	document.body.appendChild(textarea);

	let selection = document.getSelection();
	let previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

	try {
		// iOS ignores textarea.select(), so the contents are selected via a Range.
		let range = document.createRange();
		range.selectNodeContents(textarea);

		selection?.removeAllRanges();
		selection?.addRange(range);
		textarea.setSelectionRange(0, text.length);

		return document.execCommand('copy');
	} catch (e) {
		return false;
	} finally {
		document.body.removeChild(textarea);

		if (previousRange && selection) {
			selection.removeAllRanges();
			selection.addRange(previousRange);
		}
	}
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
	if (!text) {
		return false;
	}

	if (window.isSecureContext && navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch (e) {
			// Permission denied or unsupported, fall through to execCommand.
		}
	}

	return fallbackCopy(text);
}

export default copyTextToClipboard;
